import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AppContainer } from "@/components/layout/app-container";
import { TabsUnderline } from "@/components/ui/tabs-underline";
import { ConfiguracoesHeader } from "@/components/configuracoes/configuracoes-header";
import { PessoaFisicaTab } from "@/components/configuracoes/pessoa-fisica-tab";
import { PessoaJuridicaTab } from "@/components/configuracoes/pessoa-juridica-tab";
import { DocumentosTab } from "@/components/configuracoes/documentos-tab";
import { PerfilTab } from "@/components/configuracoes/perfil-tab";
import { AlterarSenhaContent } from "@/components/configuracoes/alterar-senha-content";
import { PasswordSuccessModal } from "@/components/configuracoes/password-success-modal";
import { PermissoesTab } from "@/components/configuracoes/permissoes-tab";
import { TermosDeUsoTab } from "@/components/configuracoes/termos-de-uso-tab";
import { configuracoesPFFormSchema, configuracoesPJFormSchema, ConfiguracoesPJFormData, permissionsFormSchema, PermissionsFormData } from "./schemas";
import {
  useConfiguracoesPF,
  useUpdateConfiguracoesPF,
  useConfiguracoesPJ,
  useUpdateConfiguracoesPJ,
  useUploadDocument,
  usePermissions,
  useUpdatePermissions,
} from "@/services/queries/useConfiguracoes";
import type { GeneralInformationPFFormData } from "@/components/individual-registration/schemas";

type SectionType = "Configuração de Conta" | "Alterar Senha" | "Permissões" | "Termos de Uso";
type ContentTab = "Pessoa Física" | "Pessoa Jurídica" | "Documentos" | "Perfil";

export default function ConfiguracoesPage() {
  const [activeSection, setActiveSection] = useState<SectionType>("Configuração de Conta");
  const [activeContentTab, setActiveContentTab] = useState<ContentTab>("Pessoa Física");
  const [showPasswordSuccessModal, setShowPasswordSuccessModal] = useState(false);

  // Documents state
  const [documents, setDocuments] = useState<{
    contratoSocial: File | null;
    comprovanteEnderecoPJ: File | null;
    cartaoCNPJ: File | null;
    comprovanteContaBancariaPJ: File | null;
    documentoFotoRGCNH: File | null;
    comprovanteEnderecoPF: File | null;
    drtOuSimilar: File | null;
    carteiraVacinacao: File | null;
  }>({
    contratoSocial: null,
    comprovanteEnderecoPJ: null,
    cartaoCNPJ: null,
    comprovanteContaBancariaPJ: null,
    documentoFotoRGCNH: null,
    comprovanteEnderecoPF: null,
    drtOuSimilar: null,
    carteiraVacinacao: null,
  });

  // React Query hooks - PF
  const { data: configuracoesData, isLoading } = useConfiguracoesPF();
  const updateMutation = useUpdateConfiguracoesPF();

  // React Query hooks - PJ
  const { data: configuracoesPJData } = useConfiguracoesPJ();
  const updatePJMutation = useUpdateConfiguracoesPJ();

  // React Query hooks - Documents
  const uploadMutation = useUploadDocument();

  // React Query hooks - Permissions
  const { data: permissionsData } = usePermissions();
  const updatePermissionsMutation = useUpdatePermissions();

  // Form initialization - PF
  const form = useForm<GeneralInformationPFFormData>({
    resolver: zodResolver(configuracoesPFFormSchema),
    defaultValues: {
      completeName: "",
      socialName: "",
      artisticName: "",
      role: "",
      rg: "",
      cpf: "",
      birthPlace: "",
      nationality: "",
      maritalStatus: "",
      pronouns: "",
      cellphone: "",
      tshirtNumber: "",
      residentialAddress: "",
      district: "",
      city: "",
      state: "",
      zipCode: "",
      healthPlan: "",
      planCoverage: "",
      emergencyContact: "",
      bloodType: "",
      allergies: "",
      coronavirusVaccination: "",
      dietaryRestrictions: "",
      bank: "",
      branch: "",
      checkingAccount: "",
      pixKey: "",
    },
  });

  // Form initialization - PJ
  const formPJ = useForm<ConfiguracoesPJFormData>({
    resolver: zodResolver(configuracoesPJFormSchema),
    defaultValues: {
      razaoSocial: "",
      cnpj: "",
      enderecoEmpresarial: "",
      bairroPJ: "",
      cidadeEstadoPJ: "",
      cepPJ: "",
      cnaes: "",
      inscricaoMunicipal: "",
      bancoPJ: "",
      agenciaPJ: "",
      contaCorrentePJ: "",
      chavePixPJ: "",
    },
  });

  // Form initialization - Permissions
  const formPermissions = useForm<PermissionsFormData>({
    resolver: zodResolver(permissionsFormSchema),
    defaultValues: {
      registerNewMembers1: "full_access",
      registerNewMembers2: "full_access",
      registerNewMembers3: "full_access",
      registerNewMembers4: "full_access",
      registerNewMembers5: "full_access",
      registerNewMembers6: "full_access",
      registerNewMembers7: "full_access",
      registerNewMembers8: "full_access",
    },
  });

  useEffect(() => {
    if (configuracoesData) {
      Object.keys(configuracoesData).forEach((key) => {
        const value = configuracoesData[key as keyof typeof configuracoesData];
        if (value !== undefined && value !== null) {
          form.setValue(key as keyof GeneralInformationPFFormData, value as any);
        }
      });
    }
  }, [configuracoesData, form]);

  // Populate PJ form when data is fetched
  useEffect(() => {
    if (configuracoesPJData) {
      Object.keys(configuracoesPJData).forEach((key) => {
        const value = configuracoesPJData[key as keyof typeof configuracoesPJData];
        if (value !== undefined && value !== null) {
          formPJ.setValue(key as keyof ConfiguracoesPJFormData, value as any);
        }
      });
    }
  }, [configuracoesPJData, formPJ]);

  // Populate Permissions form when data is fetched
  useEffect(() => {
    if (permissionsData) {
      Object.keys(permissionsData).forEach((key) => {
        formPermissions.setValue(key as keyof PermissionsFormData, permissionsData[key]);
      });
    }
  }, [permissionsData, formPermissions]);

  // Handle save
  const handleSave = () => {
    if (activeSection === "Permissões") {
      formPermissions.handleSubmit((data) => {
        updatePermissionsMutation.mutate(data);
      })();
    } else if (activeSection === "Alterar Senha") {
      document.getElementById("alterar-senha-submit")?.click();
    } else if (activeContentTab === "Pessoa Física") {
      form.handleSubmit((data) => {
        updateMutation.mutate(data);
      })();
    } else if (activeContentTab === "Pessoa Jurídica") {
      formPJ.handleSubmit((data) => {
        updatePJMutation.mutate(data);
      })();
    } else if (activeContentTab === "Perfil") {
      document.getElementById("perfil-submit")?.click();
    } else if (activeContentTab === "Documentos") {
      // Upload each document if it exists
      Object.entries(documents).forEach(([fieldName, file]) => {
        if (file) {
          uploadMutation.mutate({ fieldName, file });
        }
      });
    }
  };

  const handleCancel = () => {
    if (activeContentTab === "Pessoa Física" && configuracoesData) {
      Object.keys(configuracoesData).forEach((key) => {
        const value = configuracoesData[key as keyof typeof configuracoesData];
        if (value !== undefined && value !== null) {
          form.setValue(key as keyof GeneralInformationPFFormData, value as any);
        }
      });
    } else if (activeContentTab === "Pessoa Jurídica" && configuracoesPJData) {
      Object.keys(configuracoesPJData).forEach((key) => {
        const value = configuracoesPJData[key as keyof typeof configuracoesPJData];
        if (value !== undefined && value !== null) {
          formPJ.setValue(key as keyof ConfiguracoesPJFormData, value as any);
        }
      });
    } else {
      form.reset();
      formPJ.reset();
    }
  };

  // Content tabs for TabsUnderline
  const contentTabs: ContentTab[] = ["Pessoa Física", "Pessoa Jurídica", "Documentos", "Perfil"];

  // Render section content based on active section
  const renderSectionContent = () => {
    // Alterar Senha section
    if (activeSection === "Alterar Senha") {
      return (
        <AlterarSenhaContent 
          onSuccess={() => setShowPasswordSuccessModal(true)} 
          onCancel={handleCancel}
        />
      );
    }

    // Permissões section
    if (activeSection === "Permissões") {
      return (
        <PermissoesTab 
          form={formPermissions} 
          onSave={handleSave}
          onCancel={handleCancel}
          isSaving={updatePermissionsMutation.isPending}
        />
      );
    }

    // Termos de Uso section
    if (activeSection === "Termos de Uso") {
      return <TermosDeUsoTab />;
    }

    // Configuração de Conta section - show tabs
    return (
      <div className="flex flex-col gap-[30px]">
        {/* Tabs */}
        <TabsUnderline
          tabs={contentTabs}
          activeTab={activeContentTab}
          onTabChange={(tab) => setActiveContentTab(tab as ContentTab)}
        />

        {/* Tab Content */}
        {activeContentTab === "Pessoa Física" && (
          <PessoaFisicaTab 
            form={form} 
            onSave={handleSave} 
            onCancel={handleCancel} 
            isSaving={updateMutation.isPending} 
          />
        )}
        {activeContentTab === "Pessoa Jurídica" && (
          <PessoaJuridicaTab 
            form={formPJ} 
            onSave={handleSave} 
            onCancel={handleCancel} 
            isSaving={updatePJMutation.isPending} 
          />
        )}
        {activeContentTab === "Documentos" && (
          <DocumentosTab
            documents={documents}
            onDocumentChange={(field, file) => {
              setDocuments((prev) => ({ ...prev, [field]: file }));
            }}
            onSave={handleSave}
            onCancel={handleCancel}
            isSaving={uploadMutation.isPending}
          />
        )}
        {activeContentTab === "Perfil" && (
          <PerfilTab onCancel={handleCancel} />
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <AppContainer title="Configurações" description="Gerencie suas configurações">
        <div className="flex items-center justify-center py-20">
          <p className="text-white">Carregando...</p>
        </div>
      </AppContainer>
    );
  }

  return (
    <AppContainer
      title="Configurações"
    >
      {/* Sticky Header */}
      <div className="top-0 z-50 mb-[30px]">
        <ConfiguracoesHeader
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />
      </div>

      {/* Scrollable Content */}
      <div className="pb-[30px]">
        {renderSectionContent()}
      </div>

      {/* Password Success Modal */}
      <PasswordSuccessModal
        open={showPasswordSuccessModal}
        onClose={() => setShowPasswordSuccessModal(false)}
      />
    </AppContainer>
  );
}
